import { EffectType, EquipmentType, ItemType } from '@game/enums';
import type { PopulatedItem } from '@game/game-data';
import type { JSX, ReactNode } from 'react';

import { armourMinMax } from './armourMinMax';
import { weaponMinMax } from './weaponMinMax';

interface AttributesDescriptionProps {
  item: PopulatedItem;
  id: string;
  quality: number;
}

function effectValueCalculation(base: number, quality: number): number {
  return base + base * (quality / 2);
}

const WeaponDamageCalculation = ({ level, quality }: { level: number; quality: number }): JSX.Element => {
  const [minDamage, maxDamage] = weaponMinMax({ level, quality });
  return (
    <>
      {minDamage} to {maxDamage} damage
    </>
  );
};

const ArmourCalculation = ({ level, quality }: { level: number; quality: number }): JSX.Element => {
  const [minBase, maxBase] = armourMinMax({ level, quality });
  return (
    <>
      {minBase} to {maxBase} armour
    </>
  );
};

const effectGain = (min: number, max: number, quality: number): string => {
  let gain = '';
  if (min && max) {
    gain =
      min === max ? `${effectValueCalculation(min, quality)}` : `${effectValueCalculation(min, quality)} to ${effectValueCalculation(max, quality)}`;
  }

  return gain;
};

const ConsumableDescription = ({ children }: { children: ReactNode }): JSX.Element => {
  return <div className="examine-item--consumable">{children}</div>;
};

function effectDescription(effectType: EffectType, input: string | string[]): string {
  let description = effectDescriptionMap[effectType];
  if (!description) {
    return '';
  }

  if (Array.isArray(input)) {
    for (const [i, element] of input.entries()) {
      description = description.replace(`$${i}`, element);
    }
  } else {
    description = description.replace('$0', input);
  }

  return description;
}

const effectDescriptionMap: Record<EffectType, string> = {
  [EffectType.Unknown]: '',
  [EffectType.AcceptMission]: 'Starts mission $0',
  [EffectType.AddCredits]: 'Adds $0 to $1 credits',
  [EffectType.RemoveCredits]: 'Removes $0 to $1 credits',
  [EffectType.AddSkill]: 'Teaches the skill $0',
  [EffectType.RestoreEnergy]: 'Restores $0 energy',
  [EffectType.RestoreHealth]: 'Restores $0 health',
  [EffectType.RestoreSpirit]: 'Restores $0 spirit',
  [EffectType.DecreaseEnergy]: 'Removes $0 energy',
  [EffectType.DecreaseHealth]: 'Removes $0 health',
  [EffectType.DecreaseSpirit]: 'Removes $0 spirit',
  [EffectType.BuffHealth]: 'Increases health by $0 for $1 minutes',
  [EffectType.BuffEnergy]: 'Increases energy by $0 for $1 minutes',
  [EffectType.BuffSpirit]: 'Increases spirit by $0 for $1 minutes',
  [EffectType.BuffDamagePercent]: 'Increases damage by $0% for $1 minutes',
  [EffectType.BuffArmourPercent]: 'Increases armour by $0% for $1 minutes',
  [EffectType.BuffCraftingSuccessPercent]: 'Increases crafting success by $0% for $1 minutes',
  [EffectType.DebuffHealth]: 'Decreases health by $0 for $1 minutes',
  [EffectType.DebuffEnergy]: 'Decreases energy by $0 for $1 minutes',
  [EffectType.DebuffSpirit]: 'Decreases spirit by $0 for $1 minutes ',
  [EffectType.AddItem]: 'Adds item $0',
  [EffectType.AddCraftingSchematic]: 'Adds crafting schematic $0',
  [EffectType.AddModuleSlot]: 'Adds $0 module slot',
  [EffectType.RestoreHealthPercent]: 'Restores $0% health',
  [EffectType.RestoreEnergyPercent]: 'Restores $0% energy',
  [EffectType.RestoreSpiritPercent]: 'Restores $0% spirit',
  [EffectType.BuffHealthPercent]: 'Increases health by $0% for $1 minutes',
  [EffectType.BuffEnergyPercent]: 'Increases energy by $0% for $1 minutes',
  [EffectType.BuffSpiritPercent]: 'Increases spirit by $0% for $1 minutes',
  [EffectType.DebuffHealthPercent]: 'Decreases health by $0% for $1 minutes',
  [EffectType.DebuffEnergyPercent]: 'Decreases energy by $0% for $1 minutes',
  [EffectType.DebuffSpiritPercent]: 'Decreases spirit by $0% for $1 minutes',
};

export const AttributesDescription = ({ item, id, quality }: AttributesDescriptionProps): ReactNode => {
  if (item.type === ItemType.Consumable) {
    if (item.consumeEffects && item.consumeEffects.length > 0) {
      return (
        <div className="examine-item--consumables">
          {item.consumeEffects.map((consumeEffect) => {
            const key = `${id}_${quality}_${consumeEffect.type}`;
            switch (consumeEffect.type) {
              case EffectType.AcceptMission: {
                return (
                  <ConsumableDescription key={key}>
                    {effectDescription(consumeEffect.type, `&quot;${consumeEffect.missionName}&quot;`)}{' '}
                    {(consumeEffect.mission?.minLevel ?? 0) > 1 && `(Requires level ${consumeEffect.mission?.minLevel})`}
                  </ConsumableDescription>
                );
              }
              case EffectType.AddCredits:
              case EffectType.AddModuleSlot:
              case EffectType.RemoveCredits: {
                return (
                  <ConsumableDescription key={key}>
                    {effectDescription(consumeEffect.type, [String(consumeEffect.min), String(consumeEffect.max)])}
                  </ConsumableDescription>
                );
              }
              case EffectType.AddSkill: {
                return <ConsumableDescription key={key}>{effectDescription(consumeEffect.type, consumeEffect.skillName)}</ConsumableDescription>;
              }
              case EffectType.RestoreEnergy:
              case EffectType.RestoreHealth:
              case EffectType.RestoreSpirit:
              case EffectType.RestoreHealthPercent:
              case EffectType.RestoreEnergyPercent:
              case EffectType.RestoreSpiritPercent:
              case EffectType.DecreaseEnergy:
              case EffectType.DecreaseHealth:
              case EffectType.DecreaseSpirit: {
                return (
                  <ConsumableDescription key={key}>
                    {effectDescription(consumeEffect.type, effectGain(consumeEffect.min, consumeEffect.max, quality))}
                  </ConsumableDescription>
                );
              }
              case EffectType.BuffHealth:
              case EffectType.BuffEnergy:
              case EffectType.BuffSpirit:
              case EffectType.BuffHealthPercent:
              case EffectType.BuffEnergyPercent:
              case EffectType.BuffSpiritPercent:
              case EffectType.BuffDamagePercent:
              case EffectType.BuffArmourPercent:
              case EffectType.BuffCraftingSuccessPercent:
              case EffectType.DebuffHealth:
              case EffectType.DebuffEnergy:
              case EffectType.DebuffSpirit:
              case EffectType.DebuffHealthPercent:
              case EffectType.DebuffEnergyPercent:
              case EffectType.DebuffSpiritPercent: {
                return (
                  <ConsumableDescription key={key}>
                    {effectDescription(consumeEffect.type, [
                      effectGain(consumeEffect.min, consumeEffect.max, quality),
                      String(consumeEffect.duration),
                    ])}
                  </ConsumableDescription>
                );
              }
              case EffectType.AddItem: {
                return (
                  <ConsumableDescription key={key}>
                    {effectDescription(consumeEffect.type, `${consumeEffect.quantityMin} to ${consumeEffect.quantityMax} ${consumeEffect.itemName}`)}
                  </ConsumableDescription>
                );
              }
              default: {
                return null;
              }
            }
          })}
        </div>
      );
    }
    return null;
  }

  if (item.type === ItemType.Equipment) {
    switch (item.equipmentType) {
      case EquipmentType.Offhand:
      case EquipmentType.Mainhand: {
        return (
          <div className="examine-item--equipment">
            <span className="examine-item--damage">
              <WeaponDamageCalculation level={item.level} quality={quality} />
            </span>
          </div>
        );
      }

      case EquipmentType.Feet:
      case EquipmentType.Head:
      case EquipmentType.Legs:
      case EquipmentType.Torso:
      case EquipmentType.Waist: {
        return (
          <div className="examine-item--equipment">
            <span className="examine-item--armour">
              <ArmourCalculation level={item.level} quality={quality} />
            </span>
          </div>
        );
      }

      default: {
        return null;
      }
    }
  }
};
